import React, { useState, useEffect, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import {
    Box, Flex, Text, Heading, Button, Badge, HStack, VStack,
    Grid, GridItem, Spinner, Center, Divider,
    useDisclosure, useToast, Input, Select,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    FormControl, FormLabel, Textarea, FormHelperText,
    Stack, IconButton
} from '@chakra-ui/react';
import { AddIcon, CheckIcon, EditIcon, RepeatIcon } from '@chakra-ui/icons';
import { FiTrash2, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import api, { authAPI } from '../services/api';

// ── 工具 ──────────────────────────────────────────────
const getReasonLabel = (reason) => {
    const map = { SICK: '病假', PERSONAL: '事假', OTHER: '其他', ABSENT: '缺課', INTENSIVE: '加強輔導', LEAVE: '請假' };
    return map[reason] || reason;
};

const StatusBadge = ({ status }) => {
    const colorMap = { PENDING: 'gray', SCHEDULED: 'blue', COMPLETED: 'green', CANCELLED: 'red', NOSHOW: 'orange' };
    const labelMap = { PENDING: '待安排', SCHEDULED: '已排程', COMPLETED: '已完成', CANCELLED: '取消', NOSHOW: '未到' };
    return <Badge colorScheme={colorMap[status] || 'gray'}>{labelMap[status] || status}</Badge>;
};

const getTaipeiToday = () =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei' }).format(new Date());

const toDateStr = (year, month, day) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];

function buildCalendarCells(year, month) {
    const firstDay = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    const offset = firstDay.getDay();
    const cells = Array(offset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
        const dow = new Date(year, month - 1, d).getDay();
        cells.push({ day: d, dateStr: toDateStr(year, month, d), isWeekend: dow === 0 || dow === 6 });
    }
    return cells;
}

function groupByMakeupDate(records) {
    const map = {};
    records.forEach(r => {
        if (!r.makeup_date) return;
        if (!map[r.makeup_date]) map[r.makeup_date] = { scheduled: [], completed: [] };
        if (r.status === 'SCHEDULED') map[r.makeup_date].scheduled.push(r);
        else if (r.status === 'COMPLETED') map[r.makeup_date].completed.push(r);
    });
    return map;
}

const loadStudents = (input) =>
    authAPI.getStudents({ search: input, limit: 20 })
        .then(res => res.data.map(s => ({
            value: s.id,
            label: `${s.full_name} (${s.class_name || s.class?.name || '未分班'})`
        })));

// ── 主元件 ──────────────────────────────────────────────
const EnglishMakeupPage = () => {
    const toast = useToast();
    const today = getTaipeiToday();
    const [now] = useState(() => new Date());
    const [viewYear, setViewYear] = useState(now.getFullYear());
    const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
    const [isScanning, setIsScanning] = useState(false);

    // 資料
    const [pendingList, setPendingList] = useState([]);
    const [calendarRecords, setCalendarRecords] = useState([]);
    const [loadingCalendar, setLoadingCalendar] = useState(false);

    const cells = buildCalendarCells(viewYear, viewMonth);
    const numRows = Math.ceil(cells.length / 7);
    const monthStr = `${viewYear}-${String(viewMonth).padStart(2, '0')}`;
    const monthRecords = calendarRecords.filter(r => r.makeup_date && r.makeup_date.startsWith(monthStr));
    const calendarMap = groupByMakeupDate(monthRecords);

    // Drag-and-drop 狀態
    const [draggingItem, setDraggingItem] = useState(null);
    const [dragOverDate, setDragOverDate] = useState(null);

    // 單日 Modal
    const { isOpen: isDayOpen, onOpen: openDay, onClose: closeDay } = useDisclosure();
    const [dayTarget, setDayTarget] = useState(null);
    const [dayRecords, setDayRecords] = useState([]);

    // 操作 Modal（安排/完成/編輯/新增）
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [modalMode, setModalMode] = useState('SCHEDULE');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedPendingStudent, setSelectedPendingStudent] = useState(null);

    // 新增模式學生搜尋
    const [selectedStudentOption, setSelectedStudentOption] = useState(null);

    // 表單
    const [formData, setFormData] = useState({
        makeup_date: '', makeup_time: '', teaching_content: '',
        student_performance: '', status: 'SCHEDULED',
        original_date: '', absence_reason: 'LEAVE'
    });

    // ── 資料讀取 ──────────────────────────────────────────────
    const fetchPending = async () => {
        try {
            const res = await api.get('/makeup/pending');
            setPendingList(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchCalendarRecords = useCallback(async () => {
        setLoadingCalendar(true);
        try {
            const t = Date.now();
            const [schedRes, compRes] = await Promise.all([
                api.get(`/makeup/?status=SCHEDULED&limit=500&_t=${t}`),
                api.get(`/makeup/?status=COMPLETED&limit=500&_t=${t}`),
            ]);
            const records = [...(schedRes.data || []), ...(compRes.data || [])].filter(r => r.makeup_date);
            setCalendarRecords(records);
        } catch (err) { console.error(err); }
        finally { setLoadingCalendar(false); }
    }, []);

    useEffect(() => {
        fetchPending();
        fetchCalendarRecords();
    }, [fetchCalendarRecords]);

    // ── 月份切換 ──────────────────────────────────────────────
    const prevMonth = () => {
        if (viewMonth === 1) { setViewYear(y => y - 1); setViewMonth(12); }
        else setViewMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (viewMonth === 12) { setViewYear(y => y + 1); setViewMonth(1); }
        else setViewMonth(m => m + 1);
    };

    // ── 月曆格點擊 ──────────────────────────────────────────────
    const handleCellClick = (dateStr) => {
        const d = calendarMap[dateStr] || { scheduled: [], completed: [] };
        const all = [...d.scheduled, ...d.completed];
        if (all.length === 0) return;
        setDayTarget(dateStr);
        setDayRecords(all);
        openDay();
    };

    // ── Modal 開啟 ──────────────────────────────────────────────
    const handleOpenSchedule = (student, prefilledDate = '') => {
        setModalMode('SCHEDULE');
        setSelectedPendingStudent(student);
        setFormData({ makeup_date: prefilledDate, makeup_time: '17:00', teaching_content: '', student_performance: '', status: 'SCHEDULED', original_date: '', absence_reason: 'LEAVE' });
        onOpen();
    };

    const handleOpenComplete = (record) => {
        setModalMode('COMPLETE');
        setSelectedRecord(record);
        setFormData({ ...formData, status: 'COMPLETED', makeup_date: record.makeup_date, makeup_time: record.makeup_time, teaching_content: record.teaching_content || '', student_performance: record.student_performance || '' });
        closeDay();
        onOpen();
    };

    const handleOpenEdit = (record) => {
        setModalMode('EDIT');
        setSelectedRecord(record);
        setFormData({ makeup_date: record.makeup_date, makeup_time: record.makeup_time ? record.makeup_time.substring(0, 5) : '', teaching_content: record.teaching_content || '', student_performance: record.student_performance || '', status: record.status, original_date: record.original_date || '', absence_reason: record.absence_reason || 'LEAVE' });
        closeDay();
        onOpen();
    };

    const handleOpenCreate = () => {
        setModalMode('CREATE');
        setSelectedStudentOption(null);
        setFormData({ original_date: '', absence_reason: 'LEAVE', makeup_date: '', makeup_time: '17:00', teaching_content: '', student_performance: '', status: 'SCHEDULED' });
        onOpen();
    };

    const handleReasonChange = (e) => {
        const reason = e.target.value;
        const next = { ...formData, absence_reason: reason };
        if (reason === 'INTENSIVE') next.original_date = new Date().toISOString().split('T')[0];
        else if (formData.absence_reason === 'INTENSIVE') next.original_date = '';
        setFormData(next);
    };

    // ── 送出 ──────────────────────────────────────────────
    const handleSubmit = async () => {
        const prevPending = pendingList;
        const prevCalendar = calendarRecords;
        try {
            if (modalMode === 'SCHEDULE') {
                // 樂觀更新：立即移出待補課、加入月曆
                setPendingList(prev => prev.filter(s => s.makeup_record_id !== selectedPendingStudent.makeup_record_id));
                setCalendarRecords(prev => [
                    ...prev.filter(r => r.id !== selectedPendingStudent.makeup_record_id),
                    {
                        id: selectedPendingStudent.makeup_record_id,
                        status: 'SCHEDULED',
                        makeup_date: formData.makeup_date,
                        makeup_time: formData.makeup_time,
                        original_date: selectedPendingStudent.missing_date,
                        absence_reason: selectedPendingStudent.reason,
                        student: { full_name: selectedPendingStudent.full_name, id: selectedPendingStudent.student_id },
                        student_id: selectedPendingStudent.student_id,
                    }
                ]);
                onClose();
                toast({ title: '已安排補課', status: 'success' });
                await api.patch(`/makeup/${selectedPendingStudent.makeup_record_id}`, {
                    status: 'SCHEDULED',
                    makeup_date: formData.makeup_date,
                    makeup_time: formData.makeup_time,
                });
                fetchCalendarRecords();
                fetchPending();

            } else if (modalMode === 'CREATE') {
                if (!selectedStudentOption) { toast({ title: '請選擇學生', status: 'warning' }); return; }
                if (!formData.original_date) { toast({ title: '請選擇缺課/需加強日期', status: 'warning' }); return; }
                onClose();
                toast({ title: formData.makeup_date ? '已安排補課' : '已加入待補課名單', status: 'success' });
                await api.post('/makeup/', {
                    student_id: selectedStudentOption.value,
                    original_date: formData.original_date,
                    absence_reason: formData.absence_reason,
                    makeup_date: formData.makeup_date || null,
                    makeup_time: formData.makeup_time || null
                });
                fetchPending();
                fetchCalendarRecords();

            } else {
                // EDIT / COMPLETE — 樂觀更新月曆紀錄
                setCalendarRecords(prev => prev.map(r =>
                    r.id !== selectedRecord.id ? r : {
                        ...r,
                        status: formData.status,
                        teaching_content: formData.teaching_content,
                        student_performance: formData.student_performance,
                        makeup_date: modalMode === 'EDIT' ? formData.makeup_date : r.makeup_date,
                        makeup_time: modalMode === 'EDIT' ? formData.makeup_time : r.makeup_time,
                    }
                ));
                onClose();
                toast({ title: '更新成功', status: 'success' });
                await api.patch(`/makeup/${selectedRecord.id}`, {
                    status: formData.status,
                    teaching_content: formData.teaching_content,
                    student_performance: formData.student_performance,
                    makeup_date: modalMode === 'EDIT' ? formData.makeup_date : undefined,
                    makeup_time: modalMode === 'EDIT' ? formData.makeup_time : undefined
                });
                fetchCalendarRecords();
            }
        } catch (err) {
            setPendingList(prevPending);
            setCalendarRecords(prevCalendar);
            toast({ title: '操作失敗', description: err.response?.data?.detail || 'Unknown error', status: 'error' });
        }
    };

    const handleDeletePending = async (student) => {
        if (!window.confirm(`確定要將 ${student.full_name} 的此筆缺課紀錄標示為「不需補課」嗎？`)) return;
        const prevPending = pendingList;
        // 樂觀更新：立即移出待補課清單
        setPendingList(prev => prev.filter(s =>
            !(s.makeup_record_id === student.makeup_record_id && s.missing_date === student.missing_date)
        ));
        try {
            if (student.makeup_record_id) {
                await authAPI.makeup.updateRecord(student.makeup_record_id, { status: 'CANCELLED' });
            } else {
                await api.post('/makeup/', { student_id: student.student_id, original_date: student.missing_date, absence_reason: student.reason, makeup_date: null, makeup_time: null, status: 'CANCELLED' });
            }
            toast({ title: '已取消補課需求', status: 'success' });
            fetchPending();
        } catch (err) {
            setPendingList(prevPending);
            toast({ title: '操作失敗', status: 'error' });
        }
    };

    const handleCancelRecord = async (record) => {
        if (!window.confirm(`確定要取消 ${record.student?.full_name} 的補課排程嗎？`)) return;
        const prevCalendar = calendarRecords;
        // 樂觀更新：立即從月曆移除
        setCalendarRecords(prev => prev.filter(r => r.id !== record.id));
        closeDay();
        try {
            await api.patch(`/makeup/${record.id}`, { status: 'CANCELLED' });
            toast({ title: '已取消排程', status: 'success' });
            fetchCalendarRecords();
        } catch (err) {
            setCalendarRecords(prevCalendar);
            toast({ title: '操作失敗', status: 'error' });
        }
    };

    // ── Drag-and-drop ──────────────────────────────────────────────
    const handleDragStart = (e, item) => {
        setDraggingItem(item);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnd = () => {
        setDraggingItem(null);
        setDragOverDate(null);
    };

    const handleCalendarDragOver = (e, dateStr) => {
        if (!draggingItem) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverDate(dateStr);
    };

    const handleCalendarDragLeave = () => {
        setDragOverDate(null);
    };

    const handleCalendarDrop = (e, dateStr) => {
        e.preventDefault();
        setDragOverDate(null);
        if (!draggingItem) return;
        const item = draggingItem;
        setDraggingItem(null);
        handleOpenSchedule(item, dateStr);
    };

    const handleManualScan = async () => {
        setIsScanning(true);
        try {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            const dateStr = yesterday.toISOString().split('T')[0];
            const targetDate = window.prompt('請輸入要掃描的日期 (YYYY-MM-DD)', dateStr);
            if (!targetDate) { setIsScanning(false); return; }
            await authAPI.makeup.batchGenerate(targetDate);
            toast({ title: '掃描完成', status: 'success' });
            fetchPending();
        } catch (error) {
            toast({ title: '掃描失敗', description: error.response?.data?.detail || '請稍後再試', status: 'error' });
        } finally { setIsScanning(false); }
    };

    // ── Render ──────────────────────────────────────────────
    return (
        <Box h="100%" display="flex" flexDirection="column" px={4} pt={6} pb={3}>
            {/* 標題列 */}
            <HStack justify="space-between" mb={4}>
                <HStack spacing={3}>
                    <Heading size="lg">補課管理</Heading>
                    {pendingList.length > 0 && (
                        <Badge colorScheme="red" borderRadius="full" px={2} fontSize="sm">
                            待補 {pendingList.length} 人
                        </Badge>
                    )}
                </HStack>
                <HStack spacing={2}>
                    <Button leftIcon={<RepeatIcon />} size="sm" colorScheme="teal" onClick={handleManualScan} isLoading={isScanning}>
                        手動掃描
                    </Button>
                    <Button leftIcon={<AddIcon />} size="sm" colorScheme="blue" onClick={handleOpenCreate}>
                        新增補課
                    </Button>
                </HStack>
            </HStack>

            {/* 分割視圖 */}
            <Flex flex="1" gap={4} overflow="hidden" minH={0}>
                {/* 左欄（1/3）：待補課清單 */}
                <Box
                    w="33%" minW="200px" display="flex" flexDirection="column"
                    border="1.5px solid" borderColor="gray.200" borderRadius="xl"
                    bg="white" overflow="hidden"
                >
                    <Box bg="gray.50" px={4} py={3} borderBottom="1px solid" borderColor="gray.200">
                        <HStack justify="space-between">
                            <Text fontWeight="bold" fontSize="sm" color="gray.700">待補課清單</Text>
                            <Badge colorScheme={pendingList.length > 0 ? 'red' : 'gray'} borderRadius="full">
                                {pendingList.length} 人
                            </Badge>
                        </HStack>
                    </Box>
                    <Box flex="1" overflowY="auto" p={2}>
                        {pendingList.length === 0 ? (
                            <Center h="100%">
                                <VStack spacing={1}>
                                    <Text fontSize="2xl" color="gray.200">✓</Text>
                                    <Text color="gray.400" fontSize="sm">目前沒有待補課學生</Text>
                                </VStack>
                            </Center>
                        ) : (
                            <VStack spacing={2} align="stretch">
                                {pendingList.map((item) => {
                                    const id = item.makeup_record_id || `temp-${item.student_id}-${item.missing_date}`;
                                    const isDragging = draggingItem?.makeup_record_id === item.makeup_record_id && draggingItem?.missing_date === item.missing_date;
                                    return (
                                        <Box
                                            key={id} p={3} borderRadius="lg"
                                            border="1px solid" borderColor="gray.200"
                                            cursor="grab"
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, item)}
                                            onDragEnd={handleDragEnd}
                                            _hover={{ bg: 'blue.50', borderColor: 'blue.200' }}
                                            onClick={() => handleOpenSchedule(item)}
                                            opacity={isDragging ? 0.4 : 1}
                                            transition="all 0.15s"
                                        >
                                            <HStack justify="space-between" mb={1}>
                                                <Text fontWeight="bold" fontSize="sm" noOfLines={1}>{item.full_name}</Text>
                                                <IconButton
                                                    icon={<FiTrash2 />} size="xs"
                                                    colorScheme="red" variant="ghost"
                                                    onClick={(e) => { e.stopPropagation(); handleDeletePending(item); }}
                                                    aria-label="不需補課"
                                                />
                                            </HStack>
                                            <Text fontSize="xs" color="gray.500" mb={1}>{item.class_name}</Text>
                                            <HStack spacing={2}>
                                                <Text fontSize="xs" color="gray.600">{item.missing_date}</Text>
                                                <Badge colorScheme="orange" fontSize="10px">{getReasonLabel(item.reason)}</Badge>
                                            </HStack>
                                        </Box>
                                    );
                                })}
                            </VStack>
                        )}
                    </Box>
                </Box>

                {/* 右欄（2/3）：補課月曆 */}
                <Flex flex="1" flexDirection="column" minW={0}>
                    {/* 月份導覽 */}
                    <HStack justify="space-between" mb={3}>
                        <Text fontWeight="bold" color="gray.700" fontSize="sm">補課月曆</Text>
                        <HStack bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" boxShadow="xs" px={3} py={1}>
                            <IconButton icon={<FiChevronLeft />} onClick={prevMonth} size="sm" variant="ghost" aria-label="上個月" />
                            <Text fontWeight="bold" minW="110px" textAlign="center">
                                {viewYear}年 {viewMonth}月
                                {loadingCalendar && <Spinner size="xs" ml={2} color="blue.400" />}
                            </Text>
                            <IconButton icon={<FiChevronRight />} onClick={nextMonth} size="sm" variant="ghost" aria-label="下個月" />
                        </HStack>
                    </HStack>

                    {/* 月曆 */}
                    <Box flex="1" display="flex" flexDirection="column" border="1.5px solid" borderColor="gray.200" borderRadius="xl" boxShadow="sm" bg="white" overflow="hidden">
                        {/* 星期標頭 */}
                        <Grid templateColumns="repeat(7, 1fr)" gap={0} bg="gray.50" borderBottom="1.5px solid" borderColor="gray.200">
                            {WEEKDAYS.map((w, idx) => (
                                <GridItem key={w} borderLeft={idx === 0 ? 'none' : '1px solid'} borderColor="gray.100">
                                    <Text textAlign="center" fontSize="xs" fontWeight="bold" py={2}
                                        color={idx === 0 || idx === 6 ? 'blue.300' : 'gray.500'}>
                                        {w}
                                    </Text>
                                </GridItem>
                            ))}
                        </Grid>

                        {/* 日期格子 */}
                        <Grid flex="1" templateColumns="repeat(7, 1fr)" templateRows={`repeat(${numRows}, 1fr)`} gap={0}>
                            {cells.map((cell, i) => {
                                if (!cell) {
                                    const col = i % 7;
                                    return (
                                        <GridItem key={`empty-${i}`}
                                            borderRight={col < 6 ? '1px solid' : 'none'}
                                            borderBottom="1px solid" borderColor="gray.100"
                                            bg="gray.50"
                                        />
                                    );
                                }
                                const col = i % 7;
                                const { day, dateStr, isWeekend } = cell;
                                const d = calendarMap[dateStr] || { scheduled: [], completed: [] };
                                const scheduledCount = d.scheduled.length;
                                const completedCount = d.completed.length;
                                const hasAny = scheduledCount + completedCount > 0;
                                const isToday = dateStr === today;
                                const totalRows = Math.ceil(cells.length / 7);
                                const isLastRow = Math.floor(i / 7) === totalRows - 1;

                                let cellBg = isWeekend ? 'gray.50' : 'white';
                                if (isToday) cellBg = 'blue.50';
                                if (scheduledCount > 0 && completedCount > 0) cellBg = 'purple.50';
                                else if (scheduledCount > 0) cellBg = isToday ? 'blue.100' : 'blue.50';
                                else if (completedCount > 0) cellBg = isToday ? 'green.100' : 'green.50';

                                const hoverBg = scheduledCount > 0 ? 'blue.100' : completedCount > 0 ? 'green.100' : isToday ? 'blue.100' : 'gray.50';

                                return (
                                    <GridItem key={dateStr}
                                        borderRight={col < 6 ? '1px solid' : 'none'}
                                        borderBottom={!isLastRow ? '1px solid' : 'none'}
                                        borderColor="gray.100"
                                        borderTop={isToday ? '3px solid' : '0px'}
                                        borderTopColor={isToday ? 'blue.400' : 'transparent'}
                                    >
                                        <Box
                                            position="relative" h="100%"
                                            bg={dragOverDate === dateStr ? 'purple.100' : draggingItem ? 'blue.50' : cellBg}
                                            cursor={draggingItem ? 'copy' : hasAny ? 'pointer' : 'default'}
                                            border={dragOverDate === dateStr ? '2px solid' : 'none'}
                                            borderColor="purple.400"
                                            _hover={!draggingItem && hasAny ? { bg: hoverBg } : {}}
                                            onClick={() => handleCellClick(dateStr)}
                                            onDragOver={(e) => handleCalendarDragOver(e, dateStr)}
                                            onDragLeave={handleCalendarDragLeave}
                                            onDrop={(e) => handleCalendarDrop(e, dateStr)}
                                            userSelect="none" transition="background 0.1s"
                                        >
                                            <Text pos="absolute" top="6px" left="8px"
                                                fontSize="sm" fontWeight={isToday ? 'bold' : 'normal'}
                                                color={isToday ? 'blue.600' : isWeekend ? 'gray.400' : 'gray.700'}>
                                                {day}
                                            </Text>
                                            <VStack pos="absolute" bottom="5px" left="6px" spacing={0.5} align="flex-start">
                                                {scheduledCount > 0 && (
                                                    <Badge colorScheme="blue" fontSize="10px" px={1}>補課 {scheduledCount}人</Badge>
                                                )}
                                                {completedCount > 0 && (
                                                    <Badge colorScheme="green" fontSize="10px" px={1}>完成 {completedCount}人</Badge>
                                                )}
                                            </VStack>
                                        </Box>
                                    </GridItem>
                                );
                            })}
                        </Grid>
                    </Box>

                    {/* 圖例 */}
                    <HStack mt={3} px={1} spacing={6} flexWrap="wrap" gap={2}>
                        <HStack spacing={1.5}>
                            <Box w="14px" h="14px" borderRadius="sm" bg="blue.50" border="1px solid" borderColor="blue.200" />
                            <Text fontSize="xs" color="gray.500">已排定補課</Text>
                        </HStack>
                        <HStack spacing={1.5}>
                            <Box w="14px" h="14px" borderRadius="sm" bg="green.50" border="1px solid" borderColor="green.200" />
                            <Text fontSize="xs" color="gray.500">已完成</Text>
                        </HStack>
                        <HStack spacing={1.5}>
                            <Box w="14px" h="14px" borderRadius="sm" bg="blue.50" borderTop="3px solid" borderColor="blue.50" borderTopColor="blue.400" />
                            <Text fontSize="xs" color="gray.500">今日</Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.400">・拖曳左側卡片到日期格快速安排補課　・點擊卡片開啟安排視窗　・點擊月曆格查看當日詳情</Text>
                    </HStack>
                </Flex>
            </Flex>

            {/* ── 單日 Modal ── */}
            <Modal isOpen={isDayOpen} onClose={closeDay} size="md" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <HStack justify="space-between" pr={8}>
                            <Text>{dayTarget} 補課</Text>
                            <Badge colorScheme="gray">{dayRecords.length} 筆</Badge>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={4}>
                        <VStack spacing={0} align="stretch" divider={<Divider />}>
                            {dayRecords.map(record => (
                                <Box key={record.id} py={3}>
                                    <HStack justify="space-between">
                                        <Box>
                                            <HStack spacing={2} mb={1}>
                                                <Text fontWeight="bold" fontSize="sm">{record.student?.full_name}</Text>
                                                <StatusBadge status={record.status} />
                                            </HStack>
                                            <Text fontSize="xs" color="gray.500">
                                                {record.original_class?.name}　{record.makeup_time ? record.makeup_time.substring(0, 5) : '時間未定'}
                                            </Text>
                                            {record.original_date && (
                                                <Text fontSize="xs" color="gray.400">缺課日期：{record.original_date}</Text>
                                            )}
                                        </Box>
                                        <HStack spacing={1}>
                                            {record.status === 'SCHEDULED' && (
                                                <Button size="xs" colorScheme="green" leftIcon={<CheckIcon />} onClick={() => handleOpenComplete(record)}>完成</Button>
                                            )}
                                            <IconButton size="xs" icon={<EditIcon />} onClick={() => handleOpenEdit(record)} aria-label="編輯" />
                                            <IconButton size="xs" colorScheme="red" variant="ghost" icon={<FiTrash2 />} onClick={() => handleCancelRecord(record)} aria-label="取消" />
                                        </HStack>
                                    </HStack>
                                </Box>
                            ))}
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* ── 操作 Modal（安排/完成/編輯/新增）── */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {modalMode === 'SCHEDULE' && '安排補課'}
                        {modalMode === 'COMPLETE' && '完成補課與紀錄'}
                        {modalMode === 'EDIT' && '編輯補課紀錄'}
                        {modalMode === 'CREATE' && '手動新增補課'}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Stack spacing={4}>
                            {modalMode === 'CREATE' && (
                                <>
                                    <FormControl isRequired>
                                        <FormLabel>選擇學生</FormLabel>
                                        <AsyncSelect
                                            loadOptions={loadStudents}
                                            value={selectedStudentOption}
                                            onChange={setSelectedStudentOption}
                                            placeholder="輸入姓名搜尋..."
                                            isClearable
                                            noOptionsMessage={() => '輸入至少 1 個字開始搜尋'}
                                            loadingMessage={() => '搜尋中...'}
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        />
                                    </FormControl>
                                    <FormControl isRequired>
                                        <FormLabel>缺課原因</FormLabel>
                                        <Select value={formData.absence_reason} onChange={handleReasonChange}>
                                            <option value="LEAVE">請假 (LEAVE)</option>
                                            <option value="ABSENT">缺課 (ABSENT)</option>
                                            <option value="INTENSIVE">加強輔導 (INTENSIVE)</option>
                                        </Select>
                                    </FormControl>
                                    <FormControl isRequired>
                                        <FormLabel>{formData.absence_reason === 'INTENSIVE' ? '需加強日期 (預設今日)' : '原缺課日期'}</FormLabel>
                                        <Input type="date" value={formData.original_date} onChange={(e) => setFormData({ ...formData, original_date: e.target.value })} />
                                    </FormControl>
                                </>
                            )}

                            {modalMode === 'SCHEDULE' && (
                                <Box bg="gray.50" p={3} borderRadius="md">
                                    <Text><strong>學生：</strong>{selectedPendingStudent?.full_name}</Text>
                                    <Text><strong>班級：</strong>{selectedPendingStudent?.class_name}</Text>
                                    <Text><strong>缺課日期：</strong>{selectedPendingStudent?.missing_date}</Text>
                                    <Text><strong>原因：</strong>{getReasonLabel(selectedPendingStudent?.reason)}</Text>
                                </Box>
                            )}

                            {(modalMode === 'SCHEDULE' || modalMode === 'EDIT' || modalMode === 'CREATE') && (
                                <Flex gap={4}>
                                    <FormControl isRequired={modalMode !== 'CREATE'}>
                                        <FormLabel>補課日期</FormLabel>
                                        <Input type="date" value={formData.makeup_date} onChange={(e) => setFormData({ ...formData, makeup_date: e.target.value })} />
                                        {modalMode === 'CREATE' && <FormHelperText>選填，未填則列入待補課名單</FormHelperText>}
                                    </FormControl>
                                    <FormControl isRequired={modalMode !== 'CREATE'}>
                                        <FormLabel>補課時間</FormLabel>
                                        <Input type="time" value={formData.makeup_time} onChange={(e) => setFormData({ ...formData, makeup_time: e.target.value })} />
                                    </FormControl>
                                </Flex>
                            )}

                            {(modalMode === 'COMPLETE' || modalMode === 'EDIT') && (
                                <>
                                    <FormControl>
                                        <FormLabel>狀態</FormLabel>
                                        <Select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                                            <option value="SCHEDULED">已排程</option>
                                            <option value="COMPLETED">已完成</option>
                                            <option value="NOSHOW">未到</option>
                                            <option value="CANCELLED">取消</option>
                                        </Select>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>教學內容/重點</FormLabel>
                                        <Textarea placeholder="上了哪一課? 單字? 文法?" value={formData.teaching_content} onChange={e => setFormData({ ...formData, teaching_content: e.target.value })} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>學生表現/備註</FormLabel>
                                        <Textarea placeholder="學習狀況如何?" value={formData.student_performance} onChange={e => setFormData({ ...formData, student_performance: e.target.value })} />
                                    </FormControl>
                                </>
                            )}
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>取消</Button>
                        <Button colorScheme="blue" onClick={handleSubmit}>
                            {modalMode === 'SCHEDULE' ? '排程' : modalMode === 'CREATE' ? '新增' : '儲存'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default EnglishMakeupPage;
